/**
 * AgentSearch — search-pill in the host topbar that morphs into a
 * centered command palette on click / ⌘K.
 *
 * Two-element split (per plan-agent-search-positioning.md):
 *
 *   1. **Inline pill** (`.as-pill`): rendered where AgentTopbar mounts,
 *      participates in normal flex/stacking. Visible when closed.
 *   2. **Portaled morph** (`.as-search`): always mounted in
 *      `document.body`, hidden when closed. On open, captures the inline
 *      pill's rect, mounts at that rect, then transitions to centered.
 *      On close, animates back to the pill's rect and goes idle.
 *
 * The portal stays mounted at all times so the input element is always
 * in DOM — required for iOS Safari's keyboard, which only pops when
 * `focus()` is called synchronously inside the click gesture handler.
 *
 * Hard constraint per the reference component: NO Radix Dialog, NO
 * cmdk — both reset DOM on open/close. Open/close state lives in
 * `useAgentStore` so external CTAs (`useAgentSubmit`) can pop the
 * morph from anywhere; query text lives in the same store as
 * `composerText`.
 *
 * Body has three modes:
 *   - empty:    no query, no messages → SUGGESTED chips + sectioned rows + footer
 *   - querying: query text, no messages → filtered rows + "Ask agent: <q>" CTA
 *   - chatting: messages exist → ConversationView (footer hides)
 */

import { appShortcutKeys, useAppBarApps } from '@ordo/ui/app-bar'
import { Search, X } from 'lucide-react'
import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { registerAgentSearchOpener } from '../hooks/use-agent-open'
import { buildAgentChatUrl } from '../lib/agent-app-url'
import { KindIcon } from '../lib/kind-icon'
import { stripMarkdown } from '../lib/markdown'
import { useLiveSearch } from '../lib/use-live-search'
import { useAgentStore } from '../stores/agent-store'
import { useConversationContext } from '../surfaces/conversation-provider'
import type { AgentEmbedConfig, SlashCommand } from '../types'
import { agentSearchCss } from './agent-search.css'
import { ChatShell } from './chat-shell'
import { type AgentSearchItem, type AgentSearchSection, type AgentSearchSuggestion, EmptyBody } from './empty-body'
import { useVisualViewportHeight } from './keyboard-inset'
import { measureMorphAnchor, type MorphRect, triggerCloseTop } from './morph-anchor'
import { Sparkle } from './sparkle'

export interface AgentSearchProps {
  suggestions?: AgentSearchSuggestion[]
  sections?: AgentSearchSection[]
  /** Slash commands surfaced in the chat composer's autocomplete. */
  slashCommands?: SlashCommand[]
  /** Host config — drives live search-runner fan-out. When present
   *  and the user types, `config.searchable` + `config.crossApp.apps`
   *  fire in parallel and append result sections below `sections`. */
  config?: AgentEmbedConfig
  closedPlaceholder?: string
  openPlaceholder?: string
  closedWidth?: number
  openWidth?: number
  openHeight?: number
  /** Disable the global ⌘K toggle (default false). */
  disableShortcut?: boolean
  /** Render the closed state as an icon-only magnifying-glass button,
   *  matching the host's bell (for the right side of the top bar, next to
   *  it) instead of the wide centered pill. ⌘K still opens it; the open
   *  palette is the same either way. */
  compact?: boolean
  className?: string
}

/**
 * What Enter opens in search: the highlighted result, or nothing. Enter never
 * starts an agent chat from search, since every chat spends credits; "Ask
 * agent" takes a click. It used to fall back to asking the agent whenever no
 * result was highlighted, such as before the results had loaded.
 */
export function searchEnterTarget(activeIdx: number, itemCount: number): number | null {
  return activeIdx >= 0 && activeIdx < itemCount ? activeIdx : null
}

/** Corner radius of the compact icon button (`.as-pill-compact`). */
const COMPACT_RADIUS_PX = 6

/** Matches the `.as-search` width/height close transition. */
const MORPH_CLOSE_MS = 520

/** A root-relative path the host's router can resolve (not `//host/…`). */
export function isInAppHref(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//')
}

/**
 * Same-tab navigation for a palette row. An in-app href goes through the
 * host's router (`config.navigate`) at once, so the app stays mounted and the
 * morph closes over the new page. Anything else — no router given, or an
 * absolute URL such as HQ's app launcher rows — is a full page load, deferred
 * until the morph has shrunk back to a pill; without the delay the browser
 * unloads mid-animation and the user sees a frozen half-closed morph.
 */
export function openSameTab(
  href: string,
  navigate: ((href: string) => void) | undefined,
  fallback: { load: (href: string) => void; schedule: (run: () => void, ms: number) => void },
): void {
  if (navigate && isInAppHref(href)) {
    navigate(href)
    return
  }
  fallback.schedule(() => fallback.load(href), MORPH_CLOSE_MS)
}

const pageLoad = {
  load: (href: string) => {
    window.location.href = href
  },
  schedule: (run: () => void, ms: number) => {
    window.setTimeout(run, ms)
  },
}

export function AgentSearch({
  suggestions = [],
  sections = [],
  slashCommands = [],
  config,
  closedPlaceholder = 'Search or ask Agent…',
  openPlaceholder = 'Search or ask Agent…',
  closedWidth = 460,
  openWidth = 640,
  openHeight = 540,
  disableShortcut = false,
  compact = false,
  className,
}: AgentSearchProps) {
  // Mirror window.visualViewport.height into a CSS var so the mobile
  // open state precisely fits the visible area when the iOS soft
  // keyboard + form-helper-bar are up. Falls back to 100dvh when
  // unsupported (older iOS / Android). Cheap subscription; safe to
  // run even on desktop (no-op when keyboard isn't involved).
  useVisualViewportHeight()

  const conv = useConversationContext()
  const isOpen = useAgentStore((s) => s.open)
  const setOpen = useAgentStore((s) => s.setOpen)
  const query = useAgentStore((s) => s.composerText)
  const setQuery = useAgentStore((s) => s.setComposerText)

  const [activeIdx, setActiveIdx] = useState(-1)
  // Defer the createPortal call until after first client commit. Some
  // bundlers / Next.js Turbopack expose `document` during SSR but
  // `createPortal` either fails or splices the children inline (vs.
  // moving them to document.body). The result on hydration is a tree
  // mismatch — server tree had backdrop+morph as children of the
  // <AgentSearch> fragment, client tree has them at body. Use the
  // standard mounted-flag pattern: render only the inline pill on
  // first server + first client render; mount the portal on the
  // second client render after useEffect runs.
  const [portalMounted, setPortalMounted] = useState(false)
  useEffect(() => {
    setPortalMounted(true)
  }, [])
  // Stage drives the morph lifecycle:
  //   closed   — inline pill visible, portal hidden + transitions off
  //   opening  — portal mounted at pill's rect, no animation yet (one
  //              frame so the browser commits the starting rect)
  //   open     — portal at centered open dimensions, transitions on
  //   closing  — portal back to pill's rect, transition fires
  // Driven by store `open` — see the useLayoutEffect below.
  const [stage, setStage] = useState<'closed' | 'opening' | 'open' | 'closing'>('closed')
  const [pillRect, setPillRect] = useState<MorphRect | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchRef = useRef<HTMLDivElement>(null)
  const pillRef = useRef<HTMLButtonElement>(null)

  // Chat mode = there's an active conversation AND the user hasn't
  // toggled the "back to search" override. The override lets them peek
  // at the launchpad (chips / recent / jump-to) without losing the
  // chat — clicking the resume-chat row in the empty body flips it
  // back off.
  const paletteOverride = useAgentStore((s) => s.paletteOverride)
  const setPaletteOverride = useAgentStore((s) => s.setPaletteOverride)
  const conversationId = useAgentStore((s) => s.conversationId)
  const agentChatUrl = buildAgentChatUrl(conversationId, config?.agentAppUrl)
  const previousPaletteOverride = useRef(paletteOverride)
  const inChat = conv.messages.length > 0 && !paletteOverride
  const hasActiveChat = conv.messages.length > 0

  // Capture the trigger's rect (the inline pill, or the host's visible
  // search circle on phones — see pickMorphAnchor) synchronously when the
  // store flips open/closed, then drive the morph lifecycle. Synchronous
  // (`useLayoutEffect`) so the rect is correct before paint — no fly-in.
  useLayoutEffect(() => {
    if (isOpen && stage === 'closed') {
      const r = measureMorphAnchor(pillRef.current)
      if (!r) {
        // Nothing laid out yet (very early external open). Skip the
        // FLIP and just snap to centered.
        setStage('open')
        return
      }
      setPillRect(r)
      setStage('opening')
    } else if (!isOpen && (stage === 'open' || stage === 'opening')) {
      const r = measureMorphAnchor(pillRef.current)
      if (r) {
        setPillRect(r)
      }
      setStage('closing')
    }
  }, [isOpen, stage])

  // After committing the pill-rect frame, swap to the centered "open"
  // class. Two rAFs guarantees the browser has painted the starting
  // rect before the transition starts, so the spring fires from the
  // pill's exact location.
  useEffect(() => {
    if (stage !== 'opening') {
      return
    }
    let id2 = 0
    const id1 = window.requestAnimationFrame(() => {
      id2 = window.requestAnimationFrame(() => setStage('open'))
    })
    return () => {
      window.cancelAnimationFrame(id1)
      if (id2) {
        window.cancelAnimationFrame(id2)
      }
    }
  }, [stage])

  // Wait for the closing transition to finish before hiding the
  // portal. Listen on the morph element's `width` end (the longest of
  // the four-property transition); fall back to a 600ms safety timer
  // in case the transition was interrupted (e.g. user re-opens mid-close).
  useEffect(() => {
    if (stage !== 'closing') {
      return
    }
    const el = searchRef.current
    const finish = () => {
      setStage('closed')
      setPillRect(null)
    }
    const onEnd = (e: TransitionEvent) => {
      if (e.target !== el) {
        return
      }
      if (e.propertyName !== 'width') {
        return
      }
      finish()
    }
    el?.addEventListener('transitionend', onEnd)
    const t = window.setTimeout(finish, 600)
    return () => {
      el?.removeEventListener('transitionend', onEnd)
      window.clearTimeout(t)
    }
  }, [stage])

  // Live entity search — fans out to host `searchable[]` kinds and
  // cross-app federation in parallel via runSearch. Empty queries
  // and missing config short-circuit. Debounced ~250ms inside the
  // hook to match legacy host-app cadence.
  const live = useLiveSearch(query, config)

  // App launcher (PROD-1403): typing an app's name in ⌘K offers a jump to
  // it, with the same ⌘1…⌘9 numbers the app rail shows. Only while a query
  // is typed — an always-on list of every app would bury Recent / Jump To.
  const { entries: railApps } = useAppBarApps()
  const appsSection: AgentSearchSection | null = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) {
      return null
    }
    const items: AgentSearchItem[] = railApps
      .filter(({ app }) => app.name.toLowerCase().includes(q) || 'apps'.startsWith(q))
      .map(({ app, shortcut }) => ({
        id: `app-${app.id}`,
        title: app.name,
        icon: app.logo ? (
          <span className="inline-flex w-5 [&>svg]:h-auto [&>svg]:w-5">{app.logo}</span>
        ) : app.image ? (
          <img src={app.image} alt="" className="h-4 w-4 rounded-sm" />
        ) : (
          app.icon
        ),
        href: app.href,
        ...(shortcut !== null ? { shortcut: appShortcutKeys(shortcut) } : {}),
      }))
    return items.length > 0 ? { id: 'live-apps', label: 'APPS', items } : null
  }, [query, railApps])

  // Merge the static sections (Recent, Jump To, etc.) with the
  // live-search result sections. Order is fixed: static → host
  // entity hits → divider + cross-app hits.
  const enrichedSections: AgentSearchSection[] = useMemo(() => {
    const out: AgentSearchSection[] = [...sections]
    if (appsSection) {
      out.push(appsSection)
    }
    for (const hs of live.hostSections) {
      out.push({
        id: `live-host-${hs.id}`,
        label: hs.label,
        items: hs.hits.map((h) => {
          const metaValue = h.meta ?? h.subtitle
          return {
            id: `live-host-${hs.id}-${h.id}`,
            title: h.title,
            icon: <KindIcon kind={hs.id} completed={h.completed} />,
            ...(metaValue !== undefined ? { meta: metaValue } : {}),
            href: h.href,
            ...(h.completed ? { completed: true } : {}),
            ...(hs.newTab ? { target: '_blank' as const } : {}),
          }
        }),
      })
    }
    let firstCrossApp = true
    for (const cs of live.crossAppSections) {
      // cs.id is "<app>:<kindLabel>" — split on first `:` to recover kind for icon lookup.
      const kindForIcon = cs.id.includes(':') ? cs.id.split(':').slice(1).join(':') : cs.id
      out.push({
        id: `live-cross-${cs.id}`,
        label: cs.label,
        ...(firstCrossApp ? { dividerBefore: true } : {}),
        items: cs.hits.map((h) => {
          const metaValue = h.meta ?? h.subtitle
          return {
            id: `live-cross-${cs.id}-${h.id}`,
            title: h.title,
            icon: <KindIcon kind={kindForIcon} completed={h.completed} />,
            ...(metaValue !== undefined ? { meta: metaValue } : {}),
            href: h.href,
            ...(h.completed ? { completed: true } : {}),
            target: '_blank' as const,
          }
        }),
      })
      firstCrossApp = false
    }
    return out
  }, [sections, appsSection, live])

  // Flat list of selectable items in render order — used for ↑↓ nav.
  // Filter mirrors `EmptyBody`'s filter so indices stay aligned.
  // When the user has an active chat, prepend a synthetic "Resume
  // chat" item at index 0 so pressing Enter immediately on open
  // jumps back into the conversation. The visual rendering of that
  // row lives in EmptyBody; we tag activeIdx === 0 + isResume so it
  // shows the active accent.
  const flatItems: AgentSearchItem[] = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = q
      ? enrichedSections
          .map((s) =>
            s.id.startsWith('live-')
              ? s
              : {
                  ...s,
                  items: s.items.filter((i) => i.title.toLowerCase().includes(q) || s.label.toLowerCase().includes(q)),
                },
          )
          .filter((s) => s.items.length > 0)
      : enrichedSections
    const items = filtered.flatMap((s) => s.items)
    if (hasActiveChat && q.length === 0) {
      const resumeItem: AgentSearchItem = {
        id: 'resume-chat',
        title: 'Resume chat',
        onSelect: () => setPaletteOverride(false),
      }
      return [resumeItem, ...items]
    }
    return items
  }, [enrichedSections, query, hasActiveChat, setPaletteOverride])

  const open = useCallback(() => {
    // iOS Safari only opens the soft keyboard when .focus() is called
    // synchronously during the user-gesture handler. A deferred focus
    // (setTimeout, requestAnimationFrame) loses that gesture context
    // and the keyboard stays closed even though the input has focus.
    // Focus first, then trigger the morph open.
    inputRef.current?.focus()
    setOpen(true)
    setActiveIdx(flatItems.length > 0 ? 0 : -1)
  }, [flatItems.length, setOpen])

  // Host buttons (the phone search circle) open through `useAgentOpen`; route
  // them here so they get the same in-gesture focus and the keyboard.
  useEffect(() => registerAgentSearchOpener(open), [open])

  const close = useCallback(() => {
    setOpen(false)
    setActiveIdx(-1)
    // Clear the query so re-opening starts fresh — a half-typed
    // search shouldn't be lingering when the user comes back later.
    setQuery('')
    inputRef.current?.blur()
  }, [setOpen, setQuery])

  const toggle = useCallback(() => {
    if (isOpen) {
      close()
    } else {
      open()
    }
  }, [isOpen, open, close])

  // Reset active row when filter changes.
  useEffect(() => {
    if (!isOpen) {
      return
    }
    setActiveIdx(flatItems.length > 0 ? 0 : -1)
  }, [query, isOpen, flatItems.length])

  // Global ⌘K + Esc.
  useEffect(() => {
    if (disableShortcut) {
      return
    }
    const handler = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey
      if (meta && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        toggle()
      } else if (e.key === 'Escape' && isOpen) {
        e.preventDefault()
        // [PROD-89] Esc cascade — mirror the full Agent app (chat.tsx):
        // 1) agent working → interrupt the run, keep the palette open;
        // 2) queued follow-up → unwind the newest one;
        // 3) nothing to unwind → close the palette.
        if (conv.isBusy) {
          void conv.cancel()
          return
        }
        if (conv.queueCount > 0) {
          conv.popNewestQueued()
          return
        }
        close()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [disableShortcut, isOpen, toggle, close, conv])

  // Click-outside.
  useEffect(() => {
    if (!isOpen) {
      return
    }
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        close()
      }
    }
    // Defer so the same click that opened doesn't immediately close.
    const t = window.setTimeout(() => document.addEventListener('mousedown', handler), 0)
    return () => {
      window.clearTimeout(t)
      document.removeEventListener('mousedown', handler)
    }
  }, [isOpen, close])

  // When the user clicks "back" in the chat header (paletteOverride
  // flips false → true), auto-focus the search input. Two reasons:
  // (1) the user came back to search/jump-to, so they likely want to
  // type immediately; (2) on iOS the keyboard was already up from the
  // chat textarea — refocusing the search input keeps it up across
  // the transition instead of dismissing + popping again.
  useEffect(() => {
    if (paletteOverride && !previousPaletteOverride.current && isOpen) {
      requestAnimationFrame(() => inputRef.current?.focus())
    }
    previousPaletteOverride.current = paletteOverride
  }, [paletteOverride, isOpen])

  const submitQuery = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) {
        return
      }
      // Submitting always moves us into chat mode, even if the user
      // opened in palette state by default.
      setPaletteOverride(false)
      void conv.submit(trimmed)
      setActiveIdx(-1)
    },
    [conv, setPaletteOverride],
  )

  const hostNavigate = config?.navigate
  const onRowSelect = useCallback(
    (item: AgentSearchItem) => {
      if (item.href) {
        const href = item.href
        // New-tab targets (cross-app federation hits) open immediately
        // — there's no in-host transition to wait for, and deferring
        // would leave the morph hanging open while the new tab loads.
        if (item.target === '_blank') {
          window.open(href, '_blank', 'noopener,noreferrer')
          close()
          return
        }
        // Same-tab nav: trigger the close transition FIRST.
        close()
        openSameTab(href, hostNavigate, pageLoad)
        return
      }
      if (item.onSelect) {
        item.onSelect()
      }
      if (item.keepOpenOnSelect) {
        // Caller wants the morph to stay open — typical for actions
        // that switch the surface into a different mode rather than
        // navigating away (e.g. RECENT row → load + show that chat).
        return
      }
      close()
    },
    [close, hostNavigate],
  )

  const onSuggestionSelect = useCallback(
    (s: AgentSearchSuggestion) => {
      if (s.onSelect) {
        s.onSelect()
        close()
      } else {
        submitQuery(s.label)
      }
    },
    [close, submitQuery],
  )

  const handleInputKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (inChat) {
      // In chat mode ↑↓ nav is meaningless; let the textarea behave normally.
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        submitQuery(query)
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => (flatItems.length === 0 ? -1 : (i + 1) % flatItems.length))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => (flatItems.length === 0 ? -1 : (i - 1 + flatItems.length) % flatItems.length))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const index = searchEnterTarget(activeIdx, flatItems.length)
      if (index !== null && flatItems[index]) {
        onRowSelect(flatItems[index])
      }
    }
  }

  // CSS dimension vars on every render. The portal stays mounted; the
  // open palette pulls width/height from these. The closed pill renders
  // inline and reads `--as-closed-w` for its max width.
  const styleVars = {
    '--as-closed-w': `${closedWidth}px`,
    '--as-open-w': `${openWidth}px`,
    '--as-open-h': `${openHeight}px`,
  } as CSSProperties

  // Opened from the compact icon button (desktop): the morph grows out of
  // the 36px icon, so its closed layer shows that icon, not the wide pill's
  // placeholder text (which wrapped inside the tiny square), and it starts
  // with the button's 6px corners.
  const fromCompact = compact && Boolean(pillRect) && !pillRect?.fromTrigger

  // During opening + closing the morph element gets inline top/left/width/
  // height anchored to the inline pill's rect — these are the FROM frame
  // for opening and the TO frame for closing. When stage is 'open' the
  // .as-search-open CSS rule overrides them with !important; when 'closed'
  // the morph is invisible (.as-search-idle) so position doesn't matter.
  const transientStyle: CSSProperties | undefined =
    (stage === 'opening' || stage === 'closing') && pillRect
      ? {
          // Anchor the morph by its center-x so transform: translateX(-50%)
          // stays constant across all four states (no shudder).
          top: `${pillRect.top}px`,
          left: `${pillRect.left + pillRect.width / 2}px`,
          width: `${pillRect.width}px`,
          height: `${pillRect.height}px`,
          borderRadius: `${fromCompact ? COMPACT_RADIUS_PX : pillRect.borderRadius}px`,
        }
      : undefined

  const searchClasses = [
    'as-search',
    stage === 'open' ? 'as-search-open' : '',
    stage === 'opening' ? 'as-search-instant' : '',
    stage === 'closed' ? 'as-search-idle' : '',
    pillRect?.fromTrigger ? 'as-search-from-trigger' : '',
    fromCompact ? 'as-search-from-compact' : '',
    inChat && stage === 'open' ? 'as-chat-mode' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  const morph = (
    <>
      <div
        className={`as-backdrop ${stage === 'open' ? 'as-backdrop-show' : ''} ${stage === 'closed' ? 'as-backdrop-idle' : ''}`}
        aria-hidden
      />
      <div
        ref={searchRef}
        className={searchClasses}
        style={{ ...styleVars, ...transientStyle }}
        tabIndex={-1}
        role="combobox"
        aria-expanded={stage === 'open'}
        aria-haspopup="listbox"
        aria-label="Agent search">
        <div className="as-closed" aria-hidden>
          {fromCompact ? (
            <Search size={20} />
          ) : (
            <>
              <Sparkle size={16} />
              <span className="as-placeholder">{closedPlaceholder}</span>
              <span className="as-kbd">⌘K</span>
            </>
          )}
        </div>

        <div className="as-open" role="dialog" aria-modal="true">
          {inChat ? (
            // Chat mode: header at top, ConversationView in middle,
            // multi-line composer with paperclip + voice + send/stop
            // pinned at bottom. Auto-focuses on mount so re-opening
            // mid-loop is ready for the next thought.
            <ChatShell
              onClose={close}
              onBackToSearch={() => setPaletteOverride(true)}
              slashCommands={slashCommands}
              agentChatUrl={agentChatUrl}
            />
          ) : (
            // Empty / querying mode: search-palette layout with input
            // row at the top, chips + sectioned rows in the body, kbd
            // hint footer at the bottom.
            <>
              <div className="as-input-row">
                <Sparkle size={20} />
                <input
                  ref={inputRef}
                  className="as-input"
                  placeholder={openPlaceholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  autoComplete="off"
                  spellCheck={false}
                />
                <span className="as-kbd as-kbd-esc">esc</span>
                <button
                  type="button"
                  className="as-close-btn"
                  onClick={(e) => {
                    e.stopPropagation()
                    close()
                  }}
                  aria-label="Close">
                  <X />
                </button>
              </div>

              <div className="as-body">
                <EmptyBody
                  suggestions={suggestions}
                  sections={enrichedSections}
                  failedCount={live.failedCount}
                  isSearching={live.isLoading}
                  activeIdx={activeIdx}
                  onRowEnter={setActiveIdx}
                  onRowSelect={onRowSelect}
                  onSuggestionSelect={onSuggestionSelect}
                  query={query}
                  onAskAgent={submitQuery}
                  isResumeActive={hasActiveChat && query.trim().length === 0 && activeIdx === 0}
                  resumeChat={
                    hasActiveChat
                      ? (() => {
                          const last = conv.messages[conv.messages.length - 1]
                          return {
                            lastMessagePreview: stripMarkdown(last?.content ?? '').slice(0, 80),
                            lastMessageRole: (last?.role === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
                            onResume: () => {
                              setPaletteOverride(false)
                            },
                          }
                        })()
                      : undefined
                  }
                />
              </div>
            </>
          )}

          {!inChat ? (
            <div className="as-footer">
              <span>
                <span className="as-kbd as-kbd-sm">↑</span>
                <span className="as-kbd as-kbd-sm">↓</span> Navigate
              </span>
              <span>
                <span className="as-kbd as-kbd-sm">↵</span> Select
              </span>
              <span>
                <span className="as-kbd as-kbd-sm">⌘</span>
                <span className="as-kbd as-kbd-sm">K</span> Toggle
              </span>
              <span style={{ marginLeft: 'auto' }}>
                <span className="as-kbd as-kbd-sm">esc</span> Close
              </span>
            </div>
          ) : null}
        </div>
      </div>
      {pillRect?.fromTrigger && !inChat && stage !== 'closed' ? (
        // Opened from the phone search circle: the circle itself becomes the
        // close X (search icon turning into X), so the palette's own top-right
        // X is hidden. Chat mode keeps its header close; this would sit on
        // the composer.
        <button
          type="button"
          className={`as-trigger-close ${stage === 'open' ? 'as-trigger-close-on' : ''}`}
          style={{
            top: triggerCloseTop(pillRect),
            left: `${pillRect.left}px`,
            width: `${pillRect.width}px`,
            height: `${pillRect.height}px`,
          }}
          onClick={(e) => {
            e.stopPropagation()
            close()
          }}
          aria-label="Close">
          <Search className="as-trigger-close-search" />
          <X className="as-trigger-close-x" />
        </button>
      ) : null}
    </>
  )

  return (
    <>
      {/* React 19 hoists this <style> tag to document <head> on first
          render and dedupes it by `href`, so the agent-embed CSS ships
          in the SSR HTML — no flash of giant unstyled sparkle on Next
          page reloads. The previous imperative `ensureAgentSearchStyles()`
          ran only after JS hydration. */}
      <style href="ordo-agent-search-styles" precedence="default">
        {agentSearchCss}
      </style>
      <button
        ref={pillRef}
        type="button"
        className={`as-pill ${compact ? 'as-pill-compact' : ''} ${stage !== 'closed' ? 'as-pill-hidden' : ''} ${className ?? ''}`}
        style={styleVars}
        onClick={() => stage === 'closed' && open()}
        tabIndex={stage === 'closed' ? 0 : -1}
        aria-expanded={stage === 'open'}
        aria-haspopup="listbox"
        aria-label={compact ? 'Search (⌘K)' : 'Open search'}
        title={compact ? 'Search (⌘K)' : undefined}>
        {compact ? (
          // Icon-only, styled to match the host's notification bell button.
          <Search size={20} aria-hidden />
        ) : (
          <>
            <Sparkle size={16} />
            <span className="as-placeholder">{closedPlaceholder}</span>
            <span className="as-kbd">⌘K</span>
          </>
        )}
      </button>
      {portalMounted && typeof document !== 'undefined' ? createPortal(morph, document.body) : null}
    </>
  )
}

export type { AgentSearchItem, AgentSearchSection, AgentSearchSuggestion } from './empty-body'
